<div class="min-h-screen overflow-x-hidden bg-cloud-mist">

  <div class="tfr-page-shell pt-5 pb-28 lg:pb-8">
  <!-- ── Header ──────────────────────────────────────────────────────── -->
  <div class="flex items-center gap-3 pb-3">
    <button type="button" (click)="goBack()"


      aria-label="Volver al espacio" class="flex items-center justify-center h-9 w-9 rounded-xl border transition-colors focus:outline-none border-pale-gray text-slate-blue bg-snow-white">
      <lucide-icon name="arrow-left" [size]="16" />
    </button>

    @if (loading()) {
      <div class="h-5 w-44 rounded-lg animate-pulse bg-pale-gray"></div>
    } @else {
      <div class="flex-1 min-w-0">
        <h1 class="text-[15px] font-bold tracking-tight truncate text-midnight-indigo">
          {{ space()?.name ?? 'Espacio' }}
        </h1>
        <p class="text-[11px] mt-0.5 leading-none text-steel-gray">Reservar un hueco</p>
      </div>
    }
  </div>

  <!-- ── Main layout ─────────────────────────────────────────────────── -->
  <div class="flex flex-col gap-5 py-5 lg:flex-row">

    <!-- ── LEFT: mini-calendar + booking summary ─────────────────────── -->
    <aside class="lg:w-[260px] shrink-0 flex flex-col gap-4">

      <!-- Mini-calendar group (nav bar + month card as siblings) -->
      <div class="flex flex-col gap-3">

        <!-- Top nav: Hoy pill + prev/next chevrons -->
        <div class="flex items-center justify-between gap-2">
          <button type="button"
            (click)="goToCurrentMonth()"
            [disabled]="miniMonthOffset() === 0"


            aria-label="Ir al mes actual" class="h-8 px-3 flex items-center gap-1.5 rounded-xl border text-[12px] font-semibold transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-default bg-white border-pale-gray text-slate-blue">
            <lucide-icon name="calendar-days" [size]="13" />
            Hoy
          </button>
          <div class="flex items-center gap-1.5">
            <button type="button" (click)="prevMiniMonth()"
              [disabled]="miniMonthOffset() <= 0"


              aria-label="Mes anterior" class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none bg-white disabled:opacity-40 disabled:cursor-default border-pale-gray text-slate-blue">
              <lucide-icon name="chevron-left" [size]="13" />
            </button>
            <button type="button" (click)="nextMiniMonth()"
              [disabled]="miniMonthOffset() >= maxMiniMonthOffset()"
              class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none bg-white disabled:opacity-40 disabled:cursor-default"
              class="border-pale-gray text-slate-blue"
              aria-label="Mes siguiente">
              <lucide-icon name="chevron-right" [size]="13" />
            </button>
          </div>
        </div>

        <!-- Month grid card -->
        <div
             class="bg-white rounded-2xl border p-4 border-pale-gray shadow-tfr-sm">
          <p class="text-[14px] font-bold tracking-tight mb-3 text-midnight-indigo">
            {{ miniCalendar().label }}
          </p>
          <div class="grid grid-cols-7 mb-1">
          @for (h of ['L','M','X','J','V','S','D']; track h; let i = $index) {
            <span class="text-center text-[9px] font-semibold uppercase tracking-wider"
                  [style.color]="i >= 5 ? '#FDA4AF' : '#A6BBD1'">{{ h }}</span>
          }
        </div>
        <div class="flex flex-col gap-y-1">
          @for (week of miniCalendar().weeks; track $index) {
            <div class="grid grid-cols-7 rounded-lg transition-colors px-1"
                 [style.background]="isInDisplayedWeek(week[0].date) ? '#DBE7F2' : ''">
              @for (day of week; track day.dateStr) {
                <button type="button"
                  (click)="day.inMonth && !day.disabled && goToWeekOf(day.date)"
                  [disabled]="!day.inMonth || day.disabled"
                  class="flex flex-col items-center py-0.5 transition-all focus:outline-none bg-transparent disabled:cursor-default"
                  [class.opacity-20]="!day.inMonth"
                  [class.opacity-35]="day.inMonth && day.disabled"
                  [class.cursor-not-allowed]="day.inMonth && day.disabled">
                  <span class="h-5 w-5 flex items-center justify-center rounded-full text-[11px] font-medium leading-none"
                    [style.background]="day.inMonth && day.holidayType === 'national' ? '#FEE2E2'
                           : day.inMonth && day.holidayType === 'custom' ? '#FEF3C7'
                           : day.inMonth && isToday(day.date) ? '#0B3558'
                           : ''"
                    [style.color]="day.inMonth && day.holidayType === 'national' ? '#B91C1C'
                         : day.inMonth && day.holidayType === 'custom' ? '#92400E'
                         : day.inMonth && isToday(day.date) ? '#fff'
                         : day.inMonth && day.disabled ? '#C4D0DF'
                         : day.inMonth ? '#0B3558' : '#D1D5DB'"
                    [style.outline]="day.inMonth && day.holidayType !== 'none' ? '1px solid transparent' : ''">
                    {{ day.date.getDate() }}
                  </span>
                  @if (day.inMonth && day.dot !== 'none') {
                    <span class="h-1.5 w-1.5 rounded-full -mt-0.5"
                          [class.bg-green-400]="day.dot === 'green'"
                          [class.bg-steel-gray]="day.dot === 'gray'"
                          [class.bg-red-400]="day.dot === 'red'"></span>
                  } @else {
                    <span class="h-1.5 -mt-0.5"></span>
                  }
                </button>
              }
            </div>
          }
        </div>
        </div>
      </div>

      <!-- Booking summary card -->
      <div class="bg-white rounded-2xl border border-slate-200 p-5"
           style="box-shadow:rgba(71,103,136,0.04) 0 4px 5px 0;">
        @if (venue()) {
          <p class="mb-4 inline-block rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-500">
            M&iacute;nimo de {{ minHours() }} horas
          </p>
        }
        @if (hasSelection()) {
          <p class="text-[11px] font-semibold uppercase tracking-wide mb-2 text-steel-gray">Tu selección</p>
          <p class="text-[14px] font-semibold text-midnight-indigo">{{ selectedDate() }}</p>
          <p class="text-[12px] mt-0.5 text-slate-500">
            {{ selectedStart() }} – {{ selectedEnd() }}
            <span class="text-slate-400">&middot; {{ selectedHours() }} h</span>
          </p>
          @if (hourlyRate() > 0) {
            <p class="text-[11px] mt-1 text-slate-500">
              {{ formatPrice(hourlyRate()) }}/h
              @if (rateLabel()) {
                <span class="text-slate-400">&middot; {{ rateLabel() }}</span>
              }
            </p>
          }
          <div class="mt-4 flex items-baseline gap-1">
            <span class="text-[24px] font-bold text-slate-800 tabular-nums">{{ formatPrice(totalPrice()) }}</span>
            <span class="text-[11px] text-slate-400">total estimado</span>
          </div>
          @if (hasCleaning()) {
            <p class="mt-2 text-[11px] text-slate-500 leading-snug">
              Incluye limpieza profesional: {{ formatPrice(cleaningFee()) }}
            </p>
          }
          @if (showMinHoursWarning()) {
            <p class="mt-2 text-[11px] text-red-400 leading-snug">
              La reserva m&iacute;nima es de {{ minHours() }} horas
            </p>
          }
          @if (deposit() > 0) {
            <p class="mt-2 text-[11px] text-slate-500 leading-snug">
              Fianza de {{ deposit() }}&euro; (pago directo al propietario en el momento de la entrada)
            </p>
          }
          @if (error()) {
            <p class="mt-2 text-[11px] text-red-500">{{ error() }}</p>
          }
          <button type="button" (click)="onConfirmBookingClick()"
            [disabled]="submitting() || !isMinHoursMet()"
            class="mt-4 w-full py-3 rounded-xl text-[13px] font-semibold text-white bg-slate-800
                   transition-opacity disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none">
            @if (submitting()) { Reservando&hellip; } @else { Reservar }
          </button>
          <button type="button" (click)="clearSelection()"

            class="mt-2 w-full py-2 rounded-xl text-[12px] font-medium transition-colors focus:outline-none text-slate-blue">
            Quitar selección
          </button>
          @if (deposit() === 0) {
            <p class="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-slate-500 leading-snug">
              <lucide-icon name="shield-check" [size]="13" class="shrink-0" />
              <span>Fianza reembolsable gestionada de forma segura</span>
            </p>
          }
        } @else {
          <p class="text-[12px] leading-relaxed text-slate-500">
            Selecciona un hueco libre en la rejilla para iniciar tu reserva.
          </p>
        }
      </div>
    </aside>

    <!-- ── RIGHT: weekly grid ────────────────────────────────────────── -->
    <section
             class="flex-1 min-w-0 bg-white rounded-2xl border overflow-hidden border-pale-gray shadow-tfr-sm">

      <div class="flex items-center justify-between px-4 py-3 border-b border-pale-gray">
        <button type="button" (click)="prevWeek()"
          [disabled]="!canGoPrevWeek()"

          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-default border-pale-gray text-slate-blue">
          <lucide-icon name="chevron-left" [size]="14" />
        </button>
        <span class="text-[13px] font-semibold text-midnight-indigo">{{ formatWeekRange() }}</span>
        <button type="button" (click)="nextWeek()"
          [disabled]="!canGoNextWeek()"

          class="h-8 w-8 flex items-center justify-center rounded-xl border transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-default border-pale-gray text-slate-blue">
          <lucide-icon name="chevron-right" [size]="14" />
        </button>
      </div>

      <div class="overflow-x-auto">
        <div style="min-width:480px;">

          <!-- Day header -->
          <div class="flex sticky top-0 z-20 bg-white border-b border-pale-gray">
            <div class="flex items-center justify-center border-r py-3"
                 style="width:52px; flex-shrink:0; border-color:#E7EDF6;">
              <span class="text-[10px] font-medium uppercase tracking-wider text-steel-gray">Hora</span>
            </div>
            <div class="flex-1 grid" style="grid-template-columns:repeat(7,minmax(0,1fr));">
              @for (date of weekDates(); track date.toISOString(); let i = $index; let last = $last) {
                <div [class.border-r]="!last"
                     [style.background]="isToday(date) ? '#F8F9FB' : 'transparent'"
                     class="flex flex-col items-center justify-center py-2 border-pale-gray">
                  <span class="text-[9px] font-semibold uppercase tracking-wider text-steel-gray">
                    {{ weekdayLabel(date) }}
                  </span>
                  <span class="text-[13px] font-bold mt-0.5 h-6 w-6 flex items-center justify-center rounded-full"
                        [style.background]="isToday(date) ? '#0B3558' : 'transparent'"
                        [style.color]="isToday(date) ? '#fff' : '#0B3558'">
                    {{ date.getDate() }}
                  </span>
                  <!-- Full-day booking shortcut -->
                  @if (grid()[i]?.fullDayEligible) {
                    <button type="button"
                      (click)="openFullDayModal(grid()[i]!)"
                      [title]="'Reservar el día · ' + grid()[i]!.fullDayHours + ' h disponibles'"
                      class="mt-1 h-5 px-2 rounded-full flex items-center gap-1 text-[9px] font-bold
                             bg-action-blue text-white shadow-sm
                             hover:brightness-110 active:scale-95
                             transition-all focus:outline-none">
                      <span>⚡</span><span>Reservar día</span>
                    </button>
                  } @else {
                    <div class="h-5 mt-1"></div>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Grid body -->
          @if (loading()) {
            <div class="flex items-center justify-center py-20 text-steel-gray">
              <lucide-icon name="loader-circle" [size]="20" class="animate-spin mr-2" />
              <span class="text-[13px]">Cargando horarios&hellip;</span>
            </div>
          } @else if (slots().length === 0) {
            <div class="flex flex-col items-center justify-center py-16 text-center px-6">
              <lucide-icon name="clock" [size]="28" class="text-platinum-tint mb-3" />
              <p class="text-[13px] text-steel-gray">Sin horario configurado</p>
            </div>
          } @else {
            <div class="flex">

              <!-- Time-label column (schedule-step parity: inline rows, no abs) -->
              <div class="border-r border-pale-gray" style="width:52px; flex-shrink:0;">
                @for (slot of slots(); track slot.hour * 2 + slot.half; let last = $last) {
                  <div class="flex items-center justify-end pr-1.5 border-b"
                       [style.height.px]="ROW_HEIGHT"
                       [style.border-bottom-color]="last ? 'transparent' : (slot.half === 1 ? '#E7EDF6' : '#F8F9FB')"
                       [style.color]="slot.half === 0 ? '#476788' : '#C4D0DF'">
                    <span class="text-[9px] tabular-nums font-medium leading-none">
                      {{ formatHour(slot.hour, slot.half) }}
                    </span>
                  </div>
                }
              </div>

              <!-- Day columns wrapped in CSS grid -->
              <div class="flex-1 grid" style="grid-template-columns:repeat(7,minmax(0,1fr));">
                @for (col of grid(); track col.dateStr; let ci = $index; let last = $last) {
                  <div class="relative"
                       [class.border-r]="!last"
                       [style.height.px]="gridH()"
                       style="border-right-color:#E7EDF6;"
                       [style.background]="isToday(col.date) ? '#FAFBFD' : 'transparent'">

                    <!-- Layer 1 — background stripes -->
                    @for (slot of slots(); track slot.hour * 2 + slot.half; let si = $index; let lastSlot = $last) {
                      <div class="absolute w-full border-b"
                           style="z-index:1;"
                           [style.top.px]="si * ROW_HEIGHT"
                           [style.height.px]="ROW_HEIGHT"
                           [style.background-color]="slot.half === 1 ? 'rgba(247,249,251,0.7)' : 'transparent'"
                           [style.border-bottom-color]="lastSlot ? 'transparent' : (slot.half === 1 ? '#E7EDF6' : '#F8F9FB')"></div>
                    }

                    <!-- Layer 2 — past-slot overlay (diagonal stripes) -->
                    @for (slot of slots(); track slot.hour * 2 + slot.half; let si = $index) {
                      @if (col.pastMask[si]) {
                        <div class="absolute w-full pointer-events-none"
                             style="z-index:3; background-image: repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(166,187,209,0.18) 4px, rgba(166,187,209,0.18) 8px);"
                             [style.top.px]="si * ROW_HEIGHT"
                             [style.height.px]="ROW_HEIGHT"></div>
                      }
                    }

                    <!-- Layer 3 — free-slot interaction (only when bookable) -->
                    @for (slot of slots(); track slot.hour * 2 + slot.half; let si = $index) {
                      @if (col.freeMask[si]) {
                        <div class="absolute w-full cursor-pointer group"
                             style="z-index:5;"
                             [style.top.px]="si * ROW_HEIGHT"
                             [style.height.px]="ROW_HEIGHT"
                             (click)="pickSlot(col, si)">
                          <div class="absolute inset-[2px] rounded transition-all"
                               [class.opacity-100]="isSelectedSlot(col, si)"
                               [class.opacity-0]="!isSelectedSlot(col, si)"
                               [class.group-hover:opacity-100]="!isSelectedSlot(col, si)"
                               [style.background]="isSelectedSlot(col, si) ? '#006BFF' : 'rgba(0,107,255,0.10)'"></div>
                        </div>
                      }
                    }

                    <!-- Layer 4 — occupied runs.
                         Blocks (owner recurring) → midnight-indigo tint.
                         Bookings (customer)       → steel-gray.
                         No labels exposed on the public calendar. -->
                    @for (run of col.busyRuns; track run.key) {
                      <div class="absolute rounded-md overflow-hidden pointer-events-none"
                           [style.z-index]="2"
                           style="left:2px; right:2px;"
                           [style.background]="run.kind === 'block' ? blockColor : bookingColor"
                           [style.outline]="run.kind === 'block' ? '1px solid rgba(11,53,88,0.18)' : 'none'"
                           [style.top.px]="run.startIdx * ROW_HEIGHT + 2"
                           [style.height.px]="run.spanSlots * ROW_HEIGHT - 4">
                      </div>
                    }

                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </section>
  </div>

  </div>

    <!-- ── Modal: Reservar día completo ──────────────────────────────── -->
    @if (showFullDayModal()) {
      <div class="fixed inset-0 z-[120] bg-midnight-indigo/50 backdrop-blur-[1px]
                  flex items-end sm:items-center justify-center p-4 sm:p-6"
           (click)="closeFullDayModal()">
        <div class="w-full max-w-sm rounded-2xl border border-pale-gray bg-white shadow-tfr-md p-6"
             (click)="$event.stopPropagation()">

          <!-- Header -->
          <div class="flex items-center gap-3 mb-5">
            <div class="h-10 w-10 rounded-xl flex items-center justify-center bg-action-blue/10 shrink-0">
              <span class="text-[18px]">⚡</span>
            </div>
            <div class="min-w-0">
              <h3 class="text-[16px] font-bold text-midnight-indigo">Reservar día</h3>
              <p class="text-[12px] text-steel-gray">{{ formatModalDate(fdModalCol()!.date) }}</p>
            </div>
          </div>

          <!-- Minimum hours info -->
          <div class="mb-4 flex items-center gap-2 rounded-xl bg-action-blue/5 border border-action-blue/15 px-3 py-2.5">
            <span class="text-action-blue text-[13px]">ℹ︎</span>
            <p class="text-[12px] text-slate-blue leading-snug">
              Mínimo <strong class="text-midnight-indigo">{{ fullDayThreshold() }} horas</strong>
              para reserva de día completo
            </p>
          </div>

          <!-- Time selectors -->
          <div class="grid grid-cols-2 gap-3 mb-5">
            <div>
              <label class="block text-[10px] font-semibold uppercase tracking-wider text-steel-gray mb-1.5">
                Hora inicio
              </label>
              <div class="relative">
                <select
                  [value]="fdModalStart()"
                  (change)="onFdStartChange($any($event.target).value)"
                  class="w-full appearance-none rounded-xl border border-pale-gray bg-cloud-mist
                         px-3 py-2.5 text-[13px] font-medium text-midnight-indigo
                         focus:outline-none focus:ring-2 focus:ring-action-blue/20
                         focus:border-action-blue/40 cursor-pointer">
                  @for (opt of fdStartOptions(); track opt) {
                    <option [value]="opt" [selected]="fdModalStart() === opt">{{ opt }}</option>
                  }
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-steel-gray">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </span>
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-semibold uppercase tracking-wider text-steel-gray mb-1.5">
                Hora fin
              </label>
              <div class="relative">
                <select
                  [value]="fdModalEnd()"
                  (change)="fdModalEnd.set($any($event.target).value)"
                  class="w-full appearance-none rounded-xl border border-pale-gray bg-cloud-mist
                         px-3 py-2.5 text-[13px] font-medium text-midnight-indigo
                         focus:outline-none focus:ring-2 focus:ring-action-blue/20
                         focus:border-action-blue/40 cursor-pointer">
                  @for (opt of fdEndOptions(); track opt) {
                    <option [value]="opt" [selected]="fdModalEnd() === opt">{{ opt }}</option>
                  }
                </select>
                <span class="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-steel-gray">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>

          <!-- Price preview -->
          @if (fdModalHours() >= fullDayThreshold()) {
            <div class="mb-5 rounded-xl bg-cloud-mist px-4 py-3 text-center">
              <p class="text-[11px] text-steel-gray mb-1">{{ fdModalHours() }} h · precio estimado</p>
              <span class="text-[28px] font-bold text-midnight-indigo tabular-nums">
                {{ formatPrice(fdModalPrice()) }}
              </span>
              @if (cleaningFee() > 0) {
                <p class="text-[11px] text-steel-gray mt-1">
                  + limpieza {{ formatPrice(cleaningFee()) }} (opcional)
                </p>
              }
            </div>
          } @else {
            <div class="mb-5 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-center">
              <p class="text-[12px] text-red-700">
                Selecciona al menos <strong>{{ fullDayThreshold() }} h</strong> para continuar
              </p>
            </div>
          }

          <!-- Actions -->
          <div class="flex gap-2">
            <button type="button" (click)="closeFullDayModal()"
              class="flex-1 rounded-xl border border-pale-gray py-3 text-[13px] font-medium
                     text-slate-blue hover:bg-cloud-mist transition-colors focus:outline-none">
              Cancelar
            </button>
            <button type="button" (click)="confirmFullDayModal()"
              [disabled]="fdModalHours() < fullDayThreshold()"
              class="flex-1 rounded-xl bg-action-blue py-3 text-[13px] font-semibold text-white
                     hover:brightness-110 transition-all disabled:opacity-40
                     disabled:cursor-not-allowed focus:outline-none">
              Continuar →
            </button>
          </div>

        </div>
      </div>
    }

    @if (showCleaningFeeModal()) {
      <div class="fixed inset-0 z-[120] bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center p-4"
           (click)="closeCleaningFeeModal()">
        <div

             (click)="$event.stopPropagation()" class="w-full max-w-md rounded-2xl border bg-white p-8 border-pale-gray shadow-tfr-sm">
          <h3 class="text-[22px] leading-tight font-semibold text-slate-800">Disfruta de tu evento hasta el &uacute;ltimo segundo.</h3>
          <p class="mt-3 text-[13px] leading-relaxed text-slate-500">
            Convierte esto en una decisi&oacute;n de tranquilidad, no en otra tarea al final del d&iacute;a.
          </p>

          <div class="mt-5 rounded-xl bg-slate-50 px-4 py-4">
            <p class="text-[12px] text-slate-500">Tranquilidad por</p>
            <p class="mt-1 text-[24px] font-semibold text-slate-800 tabular-nums">Solo {{ formatPrice(cleaningFee()) }} adicionales</p>

            <div class="mt-4 space-y-2.5">
              <div class="flex items-start gap-2.5">
                <lucide-icon name="check" [size]="15" class="mt-0.5 text-slate-500" />
                <p class="text-[13px] leading-relaxed text-slate-500">Olv&iacute;date de fregar el suelo o vaciar las papeleras.</p>
              </div>
              <div class="flex items-start gap-2.5">
                <lucide-icon name="check" [size]="15" class="mt-0.5 text-slate-500" />
                <p class="text-[13px] leading-relaxed text-slate-500">Higienizaci&oacute;n profesional garantizada.</p>
              </div>
              <div class="flex items-start gap-2.5">
                <lucide-icon name="check" [size]="15" class="mt-0.5 text-slate-500" />
                <p class="text-[13px] leading-relaxed text-slate-500">Vete a casa y descansa; nosotros nos encargamos del resto.</p>
              </div>
            </div>
          </div>

          <div class="mt-6 flex items-center justify-end gap-2">
            <button type="button"
                    (click)="continueWithoutCleaning()"
                    class="h-10 px-4 rounded-xl border border-slate-200 bg-transparent text-[12px] font-medium text-slate-500 hover:bg-slate-50 transition-colors">
              No, yo me encargo de la limpieza
            </button>
            <button type="button"
                    (click)="acceptCleaningFeeAndContinue()"
                    class="h-10 px-4 rounded-xl bg-slate-800 text-[12px] font-semibold text-white hover:bg-slate-900 transition-colors">
              S&iacute;, a&ntilde;adir limpieza (+{{ formatPrice(cleaningFee()) }})
            </button>
          </div>
        </div>
      </div>
    }
</div>
